"use client";

import { imageApiUrl } from "@/lib/images/urls";

import dynamic from "next/dynamic";
import Image from "next/image";
import Link from "next/link";
import { useMessages } from "next-intl";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CalendarClock, ChevronDown, ChevronUp, Clock4, MapIcon, MapPin, MegaphoneIcon, Sparkles, Star, Ticket, X } from "lucide-react";

import type { SiteCopy, SiteLanguage } from "@/lib/site-data";
import { withLanguagePrefix } from "@/lib/site-locale";
import { formatDate } from "@/lib/site-date";
import type { EventRecord, EventSectionsPayload } from "@/lib/data/shared/types";
import { eventHasImage } from "@/lib/data/shared/event-has-image";
import { BandBadge } from "@/components/site/band-badge";
import { usePreferences } from "@/components/site/preferences-provider";
import { useSiteShell } from "@/components/site/site-shell";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const EventsMap = dynamic(() => import("@/components/site/events-map").then((mod) => mod.EventsMap), {
  ssr: false,
  loading: () => (
    <div className="flex h-[360px] w-full items-center justify-center rounded-2xl border border-border bg-muted text-sm text-muted-foreground">
      ...
    </div>
  ),
});

const PAGE_SIZE = 24;

function eventHref(event: { url: string | null }, language: SiteLanguage): string {
  if (event.url) return event.url;
  return withLanguagePrefix("/events", language);
}

function countryLabel(countryCode: string | null, copy: SiteCopy): string {
  const normalized = countryCode?.trim().toLowerCase();
  if (normalized === "tr") return copy.ui.countryTurkey;
  if (normalized === "us") return copy.ui.countryUnitedStates;
  if (normalized === "de") return copy.ui.countryGermany;
  if (normalized === "ua") return copy.ui.countryUkraine;
  if (normalized === "uk" || normalized === "gb") return copy.ui.countryUnitedKingdom;
  if (!normalized) return copy.ui.countryGlobal;
  return normalized.toUpperCase();
}

type EventsPagePayload = {
  items: EventRecord[];
  nextCursor: string | null;
};

/**
 * One key per shelf in ``EventSectionsPayload`` plus the order in
 * which we render them on the page. Featured leads (it is the most
 * curated of the five), followed by the time-sensitive shelves and
 * the popularity-driven Big Names rail.
 */
type ShelfKey = "featured" | "lastMinute" | "thisWeekend" | "justAnnounced" | "bigNames";

const SHELF_ORDER: readonly ShelfKey[] = [
  "featured",
  "lastMinute",
  "thisWeekend",
  "justAnnounced",
  "bigNames",
];

/**
 * Per-shelf "Show more" expansion size. Hits the per-shelf endpoint
 * ``/api/events/sections/<shelf>?limit=24``; the API caps at 60.
 */
const SHELF_EXPANDED_LIMIT = 24;

/**
 * Map a ``geocode_precision`` rung to the human-facing copy that
 * appears on the event card. We fold "region" into the city label
 * because the distinction is rarely meaningful at the user level
 * and would require a third translated string for very little
 * gain.
 */
function approximateLocationLabel(
  precision: EventRecord["geocodePrecision"],
  copy: SiteCopy,
): string | null {
  if (precision === "venue" || precision === null) return null;
  if (precision === "country") return copy.ui.approximateLocationCountry;
  if (precision === "region") return copy.ui.approximateLocationRegion;
  return copy.ui.approximateLocationCity;
}

/**
 * EventsView -- two-tier layout.
 *
 * Tier one: four curated shelves above the fold (Last Minute, This
 * Weekend, Just Announced, Big Names) followed by the legacy
 * Featured shelf. All five share a single fetch from
 * ``/api/events/sections`` so the page paints them at once.
 *
 * Tier two: a date-ascending "All upcoming events" list below the
 * shelves, infinite-scrolled from ``/api/events`` (which already
 * paginates by ``start_at`` ASC).
 *
 * The location filter from the global preferences applies
 * uniformly to every shelf and to the tail. When it is active the
 * page surfaces a banner naming the city + radius and offering a
 * one-click clear.
 */
export function EventsView({ language }: { language: SiteLanguage }) {
  const copy = useMessages() as SiteCopy;
  const { eventLocation, eventRadiusKm, clearEventLocation, hydrated } = usePreferences();
  const { openSettings } = useSiteShell();

  const [tail, setTail] = useState<EventRecord[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [tailLoading, setTailLoading] = useState(true);
  const [tailLoadingMore, setTailLoadingMore] = useState(false);
  const [tailExhausted, setTailExhausted] = useState(false);

  const [sections, setSections] = useState<EventSectionsPayload | null>(null);
  const [sectionsLoading, setSectionsLoading] = useState(true);

  // Per-shelf "Show more" cache. ``null`` means the shelf is rendering
  // the bundle's small slice; an array means the user expanded it and
  // we replaced that slice with the bigger per-shelf payload. Loading
  // state lives in ``shelfLoading`` so the button can render a busy
  // affordance without blocking the rest of the page.
  const [expandedShelves, setExpandedShelves] = useState<Partial<Record<ShelfKey, EventRecord[]>>>({});
  const [shelfLoading, setShelfLoading] = useState<Partial<Record<ShelfKey, boolean>>>({});

  const [mapOpen, setMapOpen] = useState(false);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  // Active-filter signal. Country selections filter by country code;
  // city selections filter by a radius around the selected point.
  // Keeping those modes separate avoids treating "Turkey" as "within
  // 200km of Turkey's centroid", which would hide coastal and border
  // city events from the default view.
  // Filter banner is gated on `hydrated` so the FIRST client render
  // produces exactly the SSR HTML (no banner). React raises #418 if
  // we let the banner render before the persisted preferences have
  // been copied out of localStorage by the provider's post-mount
  // effect — see PreferencesContextValue.hydrated for the rationale.
  const countryFilterActive = hydrated && Boolean(eventLocation?.countryCode && !eventLocation?.cityName);
  const radiusFilterActive = hydrated && Boolean(
    eventLocation?.cityName && eventLocation?.latitude != null && eventLocation?.longitude != null && eventRadiusKm,
  );
  const filterActive = countryFilterActive || radiusFilterActive;

  const filterParams = useMemo(() => {
    const params = new URLSearchParams();
    if (radiusFilterActive) {
      params.set("lat", String(eventLocation!.latitude!));
      params.set("lng", String(eventLocation!.longitude!));
      params.set("radiusKm", String(eventRadiusKm));
    } else if (countryFilterActive) {
      params.set("countryCode", eventLocation!.countryCode!.toLowerCase());
    }
    return params;
  }, [countryFilterActive, radiusFilterActive, eventLocation, eventRadiusKm]);

  // ----- Sections fetch (curated shelves, single shot). -----
  useEffect(() => {
    let cancelled = false;
    async function load() {
      setSectionsLoading(true);
      // Drop any per-shelf expansions when filterParams change so a
      // location swap doesn't keep the prior region's expanded payload
      // visible alongside the freshly filtered bundle.
      setExpandedShelves({});
      try {
        const response = await fetch(`/api/events/sections?${filterParams.toString()}`, { cache: "no-store" });
        if (!response.ok) throw new Error(`sections ${response.status}`);
        const payload = (await response.json()) as EventSectionsPayload;
        if (!cancelled) setSections(payload);
      } catch {
        if (!cancelled) setSections(null);
      } finally {
        if (!cancelled) setSectionsLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [filterParams]);

  /**
   * Toggle a single shelf between its bundle slice and the larger
   * per-shelf payload. We don't re-fetch when collapsing — the
   * bundle slice is already in ``sections`` and the expansion is
   * cached in ``expandedShelves`` until the next filter change.
   */
  const toggleShelf = useCallback(
    async (shelf: ShelfKey) => {
      if (expandedShelves[shelf]) {
        setExpandedShelves((current) => {
          const next = { ...current };
          delete next[shelf];
          return next;
        });
        return;
      }
      setShelfLoading((current) => ({ ...current, [shelf]: true }));
      try {
        const params = new URLSearchParams(filterParams);
        params.set("limit", String(SHELF_EXPANDED_LIMIT));
        const response = await fetch(`/api/events/sections/${shelf}?${params.toString()}`, { cache: "no-store" });
        if (!response.ok) throw new Error(`shelf ${shelf} ${response.status}`);
        const payload = (await response.json()) as { items: EventRecord[] };
        setExpandedShelves((current) => ({ ...current, [shelf]: payload.items }));
      } catch {
        // Silent — keep the collapsed bundle slice visible.
      } finally {
        setShelfLoading((current) => ({ ...current, [shelf]: false }));
      }
    },
    [expandedShelves, filterParams],
  );

  // ----- Tail fetch (date-asc, paginated). -----
  const fetchPage = useCallback(
    async (cursor: string | null, reset: boolean) => {
      if (reset) setTailLoading(true);
      else setTailLoadingMore(true);
      try {
        const params = new URLSearchParams(filterParams);
        params.set("limit", String(PAGE_SIZE));
        if (cursor) params.set("cursor", cursor);
        const response = await fetch(`/api/events?${params.toString()}`, { cache: "no-store" });
        if (!response.ok) throw new Error(`events ${response.status}`);
        const payload = (await response.json()) as EventsPagePayload;
        setTail((current) => (reset ? payload.items : [...current, ...payload.items]));
        setNextCursor(payload.nextCursor);
        setTailExhausted(payload.nextCursor == null);
      } catch {
        // Swallow — next sentinel crossing retries.
      } finally {
        if (reset) setTailLoading(false);
        else setTailLoadingMore(false);
      }
    },
    [filterParams],
  );

  useEffect(() => {
    /* eslint-disable react-hooks/set-state-in-effect --
       A filter change starts a brand-new infinite-scroll session, so
       we intentionally clear the old tail pagination state first. */
    setTail([]);
    setNextCursor(null);
    setTailExhausted(false);
    /* eslint-enable react-hooks/set-state-in-effect */
    void fetchPage(null, true);
  }, [fetchPage]);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || tailExhausted || !nextCursor) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          void fetchPage(nextCursor, false);
        }
      },
      { rootMargin: "900px 0px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [fetchPage, tailExhausted, nextCursor]);

  // ----- Aggregate map source (shelves + tail, deduped). -----
  // The map pulls every event we currently have on screen so the
  // user can see the same set the page is showing. Expanded
  // shelves win over the bundle slice when both are present so
  // newly loaded markers appear once the user clicks "Show more".
  const mapEvents = useMemo(() => {
    const dedup = new Map<string, EventRecord>();
    if (sections) {
      for (const key of SHELF_ORDER) {
        const events = expandedShelves[key] ?? sections[key].items;
        for (const event of events) {
          dedup.set(event.id, event);
        }
      }
    }
    for (const event of tail) {
      dedup.set(event.id, event);
    }
    return Array.from(dedup.values());
  }, [sections, expandedShelves, tail]);

  const focusLat = eventLocation?.latitude ?? null;
  const focusLng = eventLocation?.longitude ?? null;

  const filterLocationLabel = filterActive
    ? eventLocation?.cityLabel ?? eventLocation?.cityName ?? eventLocation?.countryName ?? ""
    : "";
  const filterBannerText = countryFilterActive
    ? copy.ui.eventsCountryFilterActive.replace("{location}", filterLocationLabel || "--")
    : copy.ui.eventsLocationFilterActive
        .replace("{location}", filterLocationLabel || "--")
        .replace("{radius}", String(eventRadiusKm));

  return (
    // The body of this view is entirely client-state-driven (location
    // filter from PreferencesProvider, shelves + tail loaded by
    // mount-time fetches). The first client render legitimately
    // differs from SSR — that's by design; we surface the persisted
    // location-filter banner the moment hydration completes, and
    // populate the shelves once the API responds. ``suppressHydration
    // Warning`` tells React not to flag the mismatch at this boundary.
    <section className="space-y-10" suppressHydrationWarning>
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="space-y-1">
          <p className="text-xs uppercase tracking-[0.18em] text-rose-600 dark:text-rose-300">duudey / {copy.nav.events}</p>
          <h1 className="text-4xl font-black leading-tight text-foreground sm:text-5xl">{copy.sections.upcomingEvents}</h1>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setMapOpen((current) => !current)}
          className="rounded-full border-border bg-muted text-foreground hover:border-rose-300/60 hover:bg-rose-400/10"
        >
          <MapIcon className="mr-1.5 size-4" aria-hidden="true" />
          {mapOpen ? copy.ui.hideMap : copy.ui.showMap}
        </Button>
      </header>

      {/* Location-filter banner. When the filter is active we show
          the city + radius and a "Clear" affordance. When it isn't
          (and we know we're past hydration), the same box flips into
          a soft prompt that opens the settings sheet so the user can
          set a location without hunting for the hamburger. We skip
          the prompt before hydration completes so SSR matches the
          first client render. */}
      {hydrated ? (
        filterActive ? (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-rose-300/40 bg-rose-400/10 px-4 py-3 text-sm">
            <p className="flex items-center gap-2 text-foreground">
              <MapPin className="size-4 text-rose-600 dark:text-rose-300" aria-hidden="true" />
              <span>{filterBannerText}</span>
            </p>
            <Button
              type="button"
              variant="outline"
              size="xs"
              className="rounded-full border-rose-300/60 bg-white/70 text-rose-700 hover:bg-white dark:bg-rose-500/10 dark:text-rose-100 dark:hover:bg-rose-500/20"
              onClick={() => clearEventLocation()}
            >
              <X className="mr-1 size-3" aria-hidden="true" />
              {copy.ui.eventsLocationFilterClear}
            </Button>
          </div>
        ) : (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-border bg-muted px-4 py-3 text-sm">
            <p className="flex items-center gap-2 text-muted-foreground">
              <MapPin className="size-4 text-muted-foreground" aria-hidden="true" />
              <span>{copy.ui.eventsLocationFilterPromptTitle}</span>
            </p>
            <Button
              type="button"
              variant="outline"
              size="xs"
              className="rounded-full border-border bg-background text-foreground hover:border-rose-300/60 hover:bg-rose-400/10"
              onClick={() => openSettings()}
            >
              <MapPin className="mr-1 size-3" aria-hidden="true" />
              {copy.ui.eventsLocationFilterPromptCta}
            </Button>
          </div>
        )
      ) : null}

      {mapOpen ? (
        <div className="space-y-2">
          <div className="h-[360px]">
            <EventsMap events={mapEvents} focusLat={focusLat} focusLng={focusLng} />
          </div>
          <p className="text-[10px] font-mono uppercase tracking-[0.16em] text-muted-foreground">
            {mapEvents.filter((row) => row.latitude != null && row.longitude != null).length} / {mapEvents.length}
          </p>
        </div>
      ) : null}

      {/* Curated shelves. We render whichever shelves came back
          non-empty and skip the others quietly — there is no
          point in headlining "Last Minute" with a "no events"
          placeholder. Featured leads, then the time-sensitive
          shelves, then Big Names. Each shelf has a "Show more"
          toggle that swaps the bundle slice for a per-shelf
          fetch with a higher limit. */}
      {sections
        ? SHELF_ORDER.map((shelf) => {
            const config = SHELF_CONFIG[shelf];
            const slice = sections[shelf];
            const expandedEvents = expandedShelves[shelf];
            const events = expandedEvents ?? slice.items;
            if (events.length === 0) return null;
            // The toggle is only meaningful when there are extra rows
            // to surface. If the bundle already exposed every event in
            // the shelf, we drop the button outright.
            const expanded = Boolean(expandedEvents);
            const showToggle = expanded || slice.hasMore;
            return (
              <ShelfSection
                key={shelf}
                icon={config.icon}
                tone={config.tone}
                title={config.title(copy)}
                intro={config.intro(copy)}
                events={events}
                copy={copy}
                language={language}
                variant={shelf === "featured" ? "featured" : "default"}
                expanded={expanded}
                onToggle={() => void toggleShelf(shelf)}
                toggling={Boolean(shelfLoading[shelf])}
                showToggle={showToggle}
              />
            );
          })
        : null}

      {/* All upcoming events. Plain date-asc list, infinite scroll. */}
      <section className="space-y-3">
        <header className="space-y-1">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1 rounded-full border border-border bg-muted px-2.5 py-0.5 text-[10px] font-mono font-semibold uppercase tracking-[0.16em] text-foreground">
              {copy.ui.upcoming}
            </span>
            <h2 className="text-lg font-black tracking-tight text-foreground sm:text-xl">
              {copy.ui.eventsAllUpcoming}
            </h2>
          </div>
          <p className="text-xs text-muted-foreground">{copy.ui.eventsAllUpcomingIntro}</p>
        </header>

        {tailLoading && !sectionsLoading && tail.length === 0 ? (
          <p className="text-sm text-muted-foreground">{copy.ui.loadingFeed}</p>
        ) : !tailLoading && tail.length === 0 && (sections === null || SHELF_ORDER.every((key) => sections[key].items.length === 0)) ? (
          <p className="text-sm text-muted-foreground">{copy.ui.noEventsForLocation}</p>
        ) : (
          <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
            {tail.map((event) => (
              <EventCard key={event.id} event={event} copy={copy} language={language} variant="default" />
            ))}
          </div>
        )}

        {!tailExhausted ? (
          <div ref={sentinelRef} className="flex justify-center py-8 text-xs font-mono uppercase tracking-[0.16em] text-muted-foreground">
            {tailLoadingMore ? copy.ui.loadingMore : ""}
          </div>
        ) : null}
      </section>
    </section>
  );
}

type ShelfTone = "amber" | "rose" | "emerald" | "violet";

/**
 * Per-shelf static metadata: chrome (icon, tone) and the i18n keys
 * used to render the heading + intro line. Keeps the render loop in
 * ``EventsView`` short and the order of shelves explicit at
 * ``SHELF_ORDER``.
 */
const SHELF_CONFIG: Record<
  ShelfKey,
  {
    icon: React.ReactNode;
    tone: ShelfTone;
    title: (copy: SiteCopy) => string;
    intro: (copy: SiteCopy) => string;
  }
> = {
  featured: {
    icon: <Sparkles className="size-3.5" aria-hidden="true" />,
    tone: "rose",
    title: (copy) => copy.ui.featuredEvents,
    intro: (copy) => copy.ui.featuredEventsIntro,
  },
  lastMinute: {
    icon: <Clock4 className="size-3.5" aria-hidden="true" />,
    tone: "amber",
    title: (copy) => copy.ui.eventsSectionLastMinute,
    intro: (copy) => copy.ui.eventsSectionLastMinuteIntro,
  },
  thisWeekend: {
    icon: <CalendarClock className="size-3.5" aria-hidden="true" />,
    tone: "rose",
    title: (copy) => copy.ui.eventsSectionThisWeekend,
    intro: (copy) => copy.ui.eventsSectionThisWeekendIntro,
  },
  justAnnounced: {
    icon: <MegaphoneIcon className="size-3.5" aria-hidden="true" />,
    tone: "emerald",
    title: (copy) => copy.ui.eventsSectionJustAnnounced,
    intro: (copy) => copy.ui.eventsSectionJustAnnouncedIntro,
  },
  bigNames: {
    icon: <Star className="size-3.5" aria-hidden="true" />,
    tone: "violet",
    title: (copy) => copy.ui.eventsSectionBigNames,
    intro: (copy) => copy.ui.eventsSectionBigNamesIntro,
  },
};

const TONE_CHIP_CLASSES: Record<ShelfTone, string> = {
  amber:
    "bg-amber-400/95 text-amber-950 dark:bg-amber-300/90 dark:text-amber-950",
  rose:
    "bg-rose-500/95 text-white",
  emerald:
    "bg-emerald-500/95 text-white",
  violet:
    "bg-violet-500/95 text-white",
};

const TONE_BORDER_CLASSES: Record<ShelfTone, string> = {
  amber: "border-amber-300/40",
  rose: "border-rose-300/40",
  emerald: "border-emerald-300/40",
  violet: "border-violet-300/40",
};

/**
 * One shelf with a tone-coloured pill, a heading, an intro line,
 * and a card grid. Featured shelves get the bigger 3-column grid
 * the legacy view used; the rest use a slightly tighter layout
 * because the content is more disposable per shelf.
 */
function ShelfSection({
  icon,
  tone,
  title,
  intro,
  events,
  copy,
  language,
  variant = "default",
  expanded,
  onToggle,
  toggling,
  showToggle,
}: {
  icon: React.ReactNode;
  tone: ShelfTone;
  title: string;
  intro: string;
  events: EventRecord[];
  copy: SiteCopy;
  language: SiteLanguage;
  variant?: "default" | "featured";
  /** True when the shelf is rendering the per-shelf expanded payload. */
  expanded: boolean;
  /** Click handler for the show-more/show-less toggle. */
  onToggle: () => void;
  /** True while the per-shelf fetch is in flight. */
  toggling: boolean;
  /**
   * False when the bundle slice already exposes every event we have
   * for this shelf — there's nothing extra to fetch, so the toggle
   * shouldn't render at all.
   */
  showToggle: boolean;
}) {
  // We don't disable the toggle mid-fetch — the user might want to
  // cancel an unfortunate click; we just relabel the busy state.
  const toggleButton = showToggle ? (
    <Button
      type="button"
      size="xs"
      variant="outline"
      className="rounded-full border-border bg-muted text-foreground hover:border-rose-300/60 hover:bg-rose-400/10"
      onClick={onToggle}
    >
      {expanded ? (
        <ChevronUp className="mr-1 size-3" aria-hidden="true" />
      ) : (
        <ChevronDown className="mr-1 size-3" aria-hidden="true" />
      )}
      {toggling
        ? copy.ui.loadingMore
        : expanded
          ? copy.ui.eventsShelfShowLess
          : copy.ui.eventsShelfShowMore}
    </Button>
  ) : null;

  return (
    <section className={cn("space-y-3 rounded-3xl border bg-card/40 p-4 sm:p-6", TONE_BORDER_CLASSES[tone])}>
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <span
              className={cn(
                "inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[10px] font-mono font-semibold uppercase tracking-[0.16em]",
                TONE_CHIP_CLASSES[tone],
              )}
            >
              {icon}
              {title}
            </span>
          </div>
          <p className="text-xs text-muted-foreground">{intro}</p>
        </div>
        {/* On ≥sm, the toggle lives in the header next to the chip
            because there's room and it groups with the title. On
            mobile we drop it from the header (hidden) and render a
            full-width copy below the grid where it's a thumb away. */}
        {toggleButton ? <div className="hidden sm:block">{toggleButton}</div> : null}
      </header>
      <div
        className={cn(
          "grid gap-5",
          variant === "featured"
            ? "md:grid-cols-2 lg:grid-cols-3"
            : "md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4",
        )}
      >
        {events.map((event) => (
          <EventCard
            key={event.id}
            event={event}
            copy={copy}
            language={language}
            variant={variant === "featured" ? "featured" : "shelf"}
          />
        ))}
      </div>
      {toggleButton ? (
        <div className="flex justify-center pt-1 sm:hidden">{toggleButton}</div>
      ) : null}
    </section>
  );
}

function EventCard({
  event,
  copy,
  language,
  variant,
}: {
  event: EventRecord;
  copy: SiteCopy;
  language: SiteLanguage;
  variant: "default" | "featured" | "shelf";
}) {
  const featured = variant === "featured";
  const compact = variant === "shelf";

  const hasMeaningfulCategory = Boolean(event.category && event.category.toLowerCase() !== "music");
  const approximateLabel = approximateLocationLabel(event.geocodePrecision, copy);

  const [showAndMore, setShowAndMore] = useState(false);

  // Track upstream image failures (ticketing CDNs occasionally 404 a
  // poster when an event is reshuffled). When this flips true we render the
  // aurora fallback instead of a broken `<Image>` so the browser's
  // console stays clean and the card keeps its rhythm.
  const [imageBroken, setImageBroken] = useState(false);

  const allEventArtists = useMemo(() => {
    const seenSpotifyIds = new Set<string>();
    const seenNames = new Set<string>();
    return event.artists.filter((artist) => {
      if (artist.spotifyId && !seenSpotifyIds.has(artist.spotifyId)) {
        seenSpotifyIds.add(artist.spotifyId);
        return true;
      }
      if (artist.name && !seenNames.has(artist.name.toLowerCase())) {
        seenNames.add(artist.name.toLowerCase());
        return true;
      }
      return false;
    });
  }, [event.artists]);

  const eventArtists = useMemo(() => {
    if (showAndMore) return allEventArtists;
    if (allEventArtists.length > 3) return allEventArtists.slice(0, 2);
    return allEventArtists;
  }, [showAndMore, allEventArtists]);

  return (
    <article className="flex flex-col overflow-hidden rounded-2xl border border-border bg-card/50 transition hover:border-rose-400/40">
      <div className="relative aspect-[16/10] w-full overflow-hidden bg-muted">
        {eventHasImage(event) && !imageBroken ? (
          <Image
            src={imageApiUrl("event", event.id)}
            alt={event.title}
            fill
            sizes={compact ? "(max-width: 768px) 100vw, 320px" : "(max-width: 768px) 100vw, 420px"}
            className="object-cover transition-transform duration-500 hover:scale-[1.03]"
            onError={() => setImageBroken(true)}
          />
        ) : (
          <div className="aurora-fallback absolute inset-0" aria-hidden="true" />
        )}
        {featured ? (
          <span className="absolute left-3 top-3 rounded-full bg-rose-500/90 px-2.5 py-0.5 text-[10px] font-mono font-semibold uppercase tracking-[0.16em] text-white">
            {copy.ui.topWire}
          </span>
        ) : null}
        {/*
          Licence attribution. Only Wikimedia Commons photography sets
          `imageCredit`, and those files are CC BY-SA with
          AttributionRequired -- rendering the image without this credit
          would breach the licence. Ticketing artwork leaves it null and
          nothing is drawn.
        */}
        {event.imageCredit && !imageBroken ? (
          <span className="absolute inset-x-0 bottom-0 bg-linear-to-t from-black/70 to-transparent px-2 pb-1 pt-4 text-[9px] leading-tight text-white/70">
            {event.imageCredit.url ? (
              <a
                href={event.imageCredit.url}
                target="_blank"
                rel="noopener noreferrer license"
                className="hover:text-white/90 hover:underline"
              >
                {event.imageCredit.text}
              </a>
            ) : (
              event.imageCredit.text
            )}
          </span>
        ) : null}
      </div>

      <div className={cn("flex flex-1 flex-col gap-2", compact ? "p-4" : "p-5")}>
        <div className="flex flex-wrap items-center gap-2 text-[10px] font-mono uppercase tracking-[0.16em] text-muted-foreground">
          <span>{formatDate(event.startAt, language)}</span>
          <span>·</span>
          <span>{(event.city ?? copy.ui.unknownCity) + " · " + countryLabel(event.countryCode, copy)}</span>
          {hasMeaningfulCategory ? (
            <span className="rounded-full border border-rose-300/40 bg-rose-400/10 px-2 py-0.5 text-rose-700 dark:text-rose-200">
              {event.category}
            </span>
          ) : null}
        </div>
        {event.url ? (
          <a
            href={eventHref(event, language)}
            target="_blank"
            rel="noreferrer"
            className={cn(
              "font-bold leading-snug text-foreground hover:text-rose-600 dark:hover:text-rose-300",
              compact ? "text-sm sm:text-base" : "text-base sm:text-lg",
            )}
          >
            {event.title}
          </a>
        ) : (
          <Link
            href={eventHref(event, language)}
            className={cn(
              "font-bold leading-snug text-foreground hover:text-rose-600 dark:hover:text-rose-300",
              compact ? "text-sm sm:text-base" : "text-base sm:text-lg",
            )}
          >
            {event.title}
          </Link>
        )}
        {event.description ? (
          <p className={cn("text-xs text-muted-foreground", compact ? "line-clamp-2" : "line-clamp-2")}>
            {event.description}
          </p>
        ) : null}
        <div className="flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
          <span>{event.venue ?? copy.ui.unknownVenue}</span>
          {approximateLabel ? (
            <span
              title={copy.ui.approximateLocation}
              className="inline-flex items-center gap-1 rounded-full border border-amber-400/40 bg-amber-400/10 px-2 py-0.5 text-[10px] font-mono uppercase tracking-[0.14em] text-amber-700 dark:border-amber-300/40 dark:bg-amber-300/10 dark:text-amber-200"
            >
              <MapPin className="size-3" aria-hidden="true" />
              {approximateLabel}
            </span>
          ) : null}
        </div>

        {event.artists.length > 0 ? (
          <div className="flex flex-wrap gap-1.5 pt-1">
            {eventArtists.map((artist, index) => (
              <BandBadge
                key={`${artist.name}-${artist.spotifyId ?? "none"}-${index}`}
                band={artist}
                language={language}
              />
            ))}
            {allEventArtists.length > eventArtists.length ? (
              <Button
                type="button"
                size="xs"
                variant="outline"
                className="h-6 rounded-full border-border bg-muted text-foreground hover:border-rose-300/60 hover:bg-rose-400/10"
                onClick={() => setShowAndMore((current) => !current)}
              >
                {`+${allEventArtists.length - eventArtists.length}`}
              </Button>
            ) : null}
          </div>
        ) : null}

        {event.url ? (
          <Button
            asChild
            size="sm"
            variant="outline"
            className="mt-auto w-full rounded-full border-border bg-muted text-foreground hover:border-rose-300/60 hover:bg-rose-400/10"
          >
            <a href={event.url} target="_blank" rel="noreferrer">
              <Ticket className="mr-1.5 size-3.5" aria-hidden="true" />
              {event.provider.name}
            </a>
          </Button>
        ) : null}
      </div>
    </article>
  );
}
